import React, { useContext, useEffect, useState } from "react";
import Papa, { ParseResult } from "papaparse";
import {
  getUserDatabase,
  saveOrUpdateReport,
  deleteImportedReports,
} from "../../Firebase";
import { OrgTreeContext } from "../../Contexts/OrgTreeContext";
import { generateRandomString } from "../../Helpers/ReportDataFunctions";
import {
  emptyReport,
} from "../../Helpers/ReportDataFunctions";
import {
  checkForUserDataImport,
  alreadyCheckedCombinations,
} from "../../Helpers/UsersCleanFunctions";
import { convertToISODate } from "../../Helpers/TimeFunctions";
import { findNodeByLabel } from "../../Helpers/OrgTreeFunctions";

export default function DataImport() {
  const orgTree = useContext(OrgTreeContext);
  const [processing, setProcessing] = useState(false);
  const [userData, setUserData] = useState<UserData[]>([]);
  const [rawData, setRawData] = useState<any[]>([]);
  const [cleanData, setCleanData] = useState<ReportData[]>([]);
  const [noUserReport, setNoUserReport] = useState<ReportData[]>([]);
  const [noLocationReport, setNoLocationReport] = useState<ReportData[]>([]);

  useEffect(() => {
    //download entire userDatabase and set to state
    getUserDatabase().then((data) => {
      console.log("User Database downloaded");
      setUserData(data);
    });
  }, []);

  function ProcessData() {
    if (rawData.length > 0 && userData && orgTree) {
      const cleanedData = transformReports(rawData, userData, orgTree);
      setCleanData(cleanedData.newReports);
      setNoUserReport(cleanedData.noFoundUserReports);
      setNoLocationReport(cleanedData.noFoundLocationReports);
    }
  }

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      Papa.parse(file, {
        complete: (result: ParseResult<any>) => {
          setRawData(result.data);
        },
        header: true,
      });
    }
  };

  const transformReports = (
    rawData: 3[],
    userDatabase: UserData[],
    orgTree: OrgTreeItem
  ) => {
    setProcessing(true);
    // Use setTimeout to allow React to update the UI before continuing
    const data = [...rawData];
    let newReports: ReportData[] = [];
    const noFoundUserReports: ReportData[] = [];
    const noFoundLocationReports: ReportData[] = [];
    setProcessing(true);
    setTimeout(() => {
      //transform data into ReportData format
      data.forEach((report: any) => {
        let cleanReport: ReportData = { ...emptyReport };
        for (let key in keysCorrespondance) {
          const validKey = key as keyof ReportData;
          keysCorrespondance[validKey].forEach((keyName: string) => {
            if (report[keyName]) {
              if (typeof cleanReport[validKey] === "string") {
                (cleanReport[validKey] as any) = report[keyName];
              }
              if (
                typeof cleanReport[validKey] === "number" &&
                typeof report[keyName] === "string"
              ) {
                (cleanReport[validKey] as any) =
                  parseFloat(cleanReport[validKey] as any) +
                  parseFloat(report[keyName]);
              }
            }
          });
        }
        newReports.push(cleanReport);
      });
      /* console.log("newReports", newReports); */

      //Check for existing user
      newReports.forEach((report, index) => {
        const reportCopy = { ...report };
        const foundUser = checkForUserDataImport(
          reportCopy.email,
          reportCopy.firstName,
          reportCopy.lastName,
          userDatabase
        );
        if (foundUser) {
          const newReportWithUser = { ...report };

          newReportWithUser.userId = foundUser.userId;
          newReportWithUser.firstName = foundUser.firstName;
          newReportWithUser.lastName = foundUser.lastName;
          switch (report.involvement) {
            case "Hillsong Church Staff":
              newReportWithUser.involvement = "churchStaff";
              break;
            case "Pastoral Care Staff":
              newReportWithUser.involvement = "pastoralCare";
              break;
            case "Pastoral Care Volunteer":
              newReportWithUser.involvement = "volunteer";
              break;
            case "Volunteer":
              newReportWithUser.involvement = "volunteer";
              break;
            default:
              newReportWithUser.involvement = "volunteer";
          }
          newReports[index] = newReportWithUser;
        } else {
          noFoundUserReports.push(report);
          newReports = newReports.filter((_, i) => i !== index);
        }
      });

      //check for location
      newReports.forEach((report, index) => {
        const matchingLocation = findNodeByLabel([orgTree], report.location);
        if (matchingLocation) {
          report.teamId = matchingLocation.teamId;
        } else {
          noFoundLocationReports.push(report);
          newReports = newReports.filter((_, i) => i !== index);
        }
      });

      //format
      newReports.forEach((report, index) => {
        let formatedReport = { ...report };
        formatedReport.importedData = true;
        formatedReport.reportId = generateRandomString();
        formatedReport.lastEditDate = new Date().toString();
        formatedReport.reportPeriodDate = new Date(
          convertToISODate(formatedReport.reportPeriodDate)
        ).toString();
        //erase all empty fields
        for (let key in formatedReport) {
          const typedKey = key as keyof ReportData;
          if (typeof formatedReport[typedKey] == "number") {
            (formatedReport[typedKey] as any) =
              Math.round(
                (report[typedKey as keyof typeof report] as number) * 10
              ) / 10;
            if (
              formatedReport[typedKey] === 0 &&
              typedKey !== "totalPastoralHours" &&
              typedKey !== "totalInPersonHours"
            ) {
              delete formatedReport[typedKey];
            }
          }
        }
        newReports[index] = formatedReport;
      });

      console.log("new checked Combinations", alreadyCheckedCombinations);
      setProcessing(false);
    }, 0);
    return {
      newReports: newReports,
      noFoundUserReports: noFoundUserReports,
      noFoundLocationReports: noFoundLocationReports,
    };
  };

  function checkCleanData() {
    if (cleanData) {
      const csv = Papa.unparse(cleanData, {
        columns: [
          "reportPeriodDate",
          "reportId",
          "bucketId",
          "lastEditDate",
          "userId",
          "firstName",
          "lastName",
          "email",
          "location",
          "teamId",
          "involvement",
          "importedData",
          "hotTopics",
          "adminAndMeetingsHours",
          "professionalDevelopmentHours",
          "crisisHours",
          "relationshipHours",
          "lifeSkillsHours",
          "traumaHours",
          "addNeedsHours",
          "healthHours",
          "mentalHealthHours",
          "prepAndEnrichAppointments",
          "weedingsConducted",
          "newPornAddictions",
          "newDFVIncidents",
          "funeralsConducted",
          "hospitalVisits",
          "suicidalIdeation",
          "nonSuicidalSelfInjury",
          "giftsFlowers",
          "meals",
          "kiloOfKindnessBags",
          "lostJobs",
          "employmentReceived",
          "totalPastoralHours",
          "totalInPersonHours",
        ],
      });
      const blob = new Blob([csv], { type: "text/csv" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.setAttribute("hidden", "");
      a.setAttribute("href", url);
      a.setAttribute("download", "report_data_Check.csv");
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    } else {
      console.log("noMatchingData");
    }
  }

  function checkNoUserData() {
    if (cleanData) {
      const csv = Papa.unparse(noUserReport, {
        columns: [
          "reportPeriodDate",
          "reportId",
          "bucketId",
          "lastEditDate",
          "userId",
          "firstName",
          "lastName",
          "email",
          "location",
          "teamId",
          "involvement",
          "importedData",
          "hotTopics",
          "adminAndMeetingsHours",
          "professionalDevelopmentHours",
          "crisisHours",
          "relationshipHours",
          "lifeSkillsHours",
          "traumaHours",
          "addNeedsHours",
          "healthHours",
          "mentalHealthHours",
          "prepAndEnrichAppointments",
          "weedingsConducted",
          "newPornAddictions",
          "newDFVIncidents",
          "funeralsConducted",
          "hospitalVisits",
          "suicidalIdeation",
          "nonSuicidalSelfInjury",
          "giftsFlowers",
          "meals",
          "kiloOfKindnessBags",
          "lostJobs",
          "employmentReceived",
          "totalPastoralHours",
          "totalInPersonHours",
        ],
      });
      const blob = new Blob([csv], { type: "text/csv" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.setAttribute("hidden", "");
      a.setAttribute("href", url);
      a.setAttribute("download", "report_data_Check.csv");
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    } else {
      console.log("noMatchingData");
    }
  }
  function checkNoLocationData() {
    if (cleanData) {
      const csv = Papa.unparse(noLocationReport, {
        columns: [
          "reportPeriodDate",
          "reportId",
          "bucketId",
          "lastEditDate",
          "userId",
          "firstName",
          "lastName",
          "email",
          "location",
          "teamId",
          "involvement",
          "importedData",
          "hotTopics",
          "adminAndMeetingsHours",
          "professionalDevelopmentHours",
          "crisisHours",
          "relationshipHours",
          "lifeSkillsHours",
          "traumaHours",
          "addNeedsHours",
          "healthHours",
          "mentalHealthHours",
          "prepAndEnrichAppointments",
          "weedingsConducted",
          "newPornAddictions",
          "newDFVIncidents",
          "funeralsConducted",
          "hospitalVisits",
          "suicidalIdeation",
          "nonSuicidalSelfInjury",
          "giftsFlowers",
          "meals",
          "kiloOfKindnessBags",
          "lostJobs",
          "employmentReceived",
          "totalPastoralHours",
          "totalInPersonHours",
        ],
      });
      const blob = new Blob([csv], { type: "text/csv" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.setAttribute("hidden", "");
      a.setAttribute("href", url);
      a.setAttribute("download", "report_data_Check.csv");
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    } else {
      console.log("noMatchingData");
    }
  }

  function uploadToServer() {
    /* uploadImportReports(cleanData).then(() => {
      console.log("upload done");
    }); */

    cleanData.forEach((report) => {
      setTimeout(() => {
        saveOrUpdateReport(report);
      }, 0.2);
    });
  }

  return (
    <div>
      <input type="file" accept=".csv" onChange={handleFileChange} />
      {rawData.length > 0 && (
        <>
          <p>Raw Reports: {rawData.length}</p>
          <button onClick={() => ProcessData()}>Process Data</button>
          {cleanData.length > 0 && userData.length > 0 && (
            <>
              <p>Clean Reports: {cleanData.length}</p>
              <p>No User Found Reports: {noUserReport.length}</p>
              <p>No Location Found Reports: {noLocationReport.length}</p>
              {processing ? (
                <p>Processing ...</p>
              ) : (
                <>
                  <button onClick={() => checkCleanData()}>Check Data</button>
                  <button onClick={() => checkNoUserData()}>
                    Check No User Data
                  </button>
                  <button onClick={() => checkNoLocationData()}>
                    Check No Location Data
                  </button>
                  <button onClick={() => uploadToServer()}>Upload Data</button>
                </>
              )}
            </>
          )}
        </>
      )}
      <button onClick={() => deleteImportedReports()}>
        Delete Imported Reports
      </button>
    </div>
  );
}
const numberKeys = [
  "adminAndMeetingsHours",
  "professionalDevelopmentHours",
  "crisisHours",
  "relationshipHours",
  "lifeSkillsHours",
  "traumaHours",
  "healthHours",
  "mentalHealthHours",
  "addNeedsHours",
  "prepAndEnrichAppointments",
  "weedingsConducted",
  "newPornAddictions",
  "newDFVIncidents",
  "funeralsConducted",
  "hospitalVisits",
  "suicidalIdeation",
  "nonSuicidalSelfInjury",
  "giftsFlowers",
  "meals",
  "kiloOfKindnessBags",
  "lostJobs",
  "employmentReceived",
  "totalPastoralHours",
  "totalInPersonHours",
];

const keysCorrespondance: Record<keyof ReportData, string[]> = {
  reportPeriodDate: ["Time"],
  firstName: ["firstName", "First Name", "Name (First)"],
  lastName: ["lastName", "Last Name", "Name (Last)"],
  email: ["email", "Email"],
  location: ["Location", "location", "LOCATIONS"],
  involvement: ["Are you:"],
  adminAndMeetingsHours: [
    "Hours spent on Administration",
    "Hours spent in Meetings (Staff meeting, team meetings etc)",
  ],
  professionalDevelopmentHours: [
    "Please input any hours spent on professional development",
  ],
  crisisHours: [
    "Crisis Hours",
    "Complaint Hours (Including calls, emails, texts and in person)",
    "Grief & Loss - Face to Face & Call Hours",
    "Grief & Loss - Written Communication Hours (Text & Email) ",
    "GRIEF/CRISIS 30min",
    "GRIEF/CRISIS 60min",
    "Number of hours at a Critical/Emergency Incident ",
  ],
  relationshipHours: [
    "Relationships -  Face to Face & Call Hours",
    "Relationships - Written Communication Hours (Text & Email) ",
  ],
  lifeSkillsHours: [
    "Life Skills - Face to Face & Call Hours",
    "Life Skills - Written Communication Hours (Text & Email) ",
  ],
  traumaHours: [
    "Trauma - Written Communication Hours (Text & Email) ",
    "Trauma - Face to Face & Call Hours",
  ],
  healthHours: [
    "Health - Face to Face & Call Hours",
    "Health - Written Communication Hours (Text & Email) ",
  ],
  mentalHealthHours: [
    "Mental Health - Written Communication Hours (Text & Email) ",
    "Mental Health - Face to Face & Call Hours",
  ],
  addNeedsHours: [
    "Additional Needs - Face to Face & Call Hours",
    "Additional Needs - Written Communication Hours (Text & Email) ",
  ],

  prepAndEnrichAppointments: ["Number of Prepare and Enrich Appointments "],
  weedingsConducted: [
    "PASTORAL CARE OVERSIGHT ONLY -  Number of Weddings CONDUCTED by your team",
  ],
  newPornAddictions: ["Pornography Addictions"],
  newDFVIncidents: ["Number of DFV situations involved in THIS WEEK"],
  funeralsConducted: [
    "PC Oversight  to fill only - Number of funerals CONDUCTED by your team",
  ],
  hospitalVisits: ["Hospital Visits"],
  suicidalIdeation: ["Suicidal Ideation"],
  nonSuicidalSelfInjury: ["Non-Suicidal Self Injury"],
  sucideDeath: [],
  childDeath: [],
  stillBorn: [],
  giftsFlowers: ["GIFTS / FLOWERS"],
  meals: ["MEALS"],
  kiloOfKindnessBags: ["KILO OF KINDNESS BAG"],
  lostJobs: ["Lost Jobs"],
  employmentReceived: ["Received employment after losing their job?"],

  totalPastoralHours: [
    "Crisis Hours",
    "Complaint Hours (Including calls, emails, texts and in person)",
    "Grief & Loss - Face to Face & Call Hours",
    "Grief & Loss - Written Communication Hours (Text & Email) ",
    "GRIEF/CRISIS 30min",
    "GRIEF/CRISIS 60min",
    "Number of hours at a Critical/Emergency Incident ",
    "Relationships -  Face to Face & Call Hours",
    "Relationships - Written Communication Hours (Text & Email) ",
    "Life Skills - Face to Face & Call Hours",
    "Life Skills - Written Communication Hours (Text & Email) ",
    "Trauma - Written Communication Hours (Text & Email) ",
    "Trauma - Face to Face & Call Hours",
    "Health - Face to Face & Call Hours",
    "Health - Written Communication Hours (Text & Email) ",
    "Mental Health - Written Communication Hours (Text & Email) ",
    "Mental Health - Face to Face & Call Hours",
    "Additional Needs - Face to Face & Call Hours",
    "Additional Needs - Written Communication Hours (Text & Email) ",
  ],
  totalInPersonHours: [
    "Crisis Hours",
    "Complaint Hours (Including calls, emails, texts and in person)",
    "Grief & Loss - Face to Face & Call Hours",
    "GRIEF/CRISIS 30min",
    "GRIEF/CRISIS 60min",
    "Number of hours at a Critical/Emergency Incident ",
    "Relationships -  Face to Face & Call Hours",
    "Life Skills - Face to Face & Call Hours",
    "Trauma - Face to Face & Call Hours",
    "Health - Face to Face & Call Hours",
    "Mental Health - Face to Face & Call Hours",
    "Additional Needs - Face to Face & Call Hours",
  ],
  reportId: ["reportId"],
  userId: ["userId"],
  bucketId: ["bucketId"],
  teamId: ["teamId"],
  lastEditDate: ["lastEditDate"],
  importedData: ["importedData"],
  hotTopics: ["Hot Topics"],
};


